import { Card, Col, Form, FormValues, Row, Switch } from 'antd';
import { subWeeks } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import CustomButton from '../../../../shared/components/buttons/custom-button/custom-button';
import DatePicker from '../../../../shared/components/date-picker/date-picker';
import LineChart from '../../../../shared/components/line-chart/line-chart';
import ClientUsersSelect from '../../../client-users/components/client-users-select/client-users-select';
import { useLazyGetDashboardSignRequestsQuery } from '../../api/dashboard-api';
import { dashboardStatisticsToChartData } from '../../utils/dashboard-statistics-to-chart-data';
import limitDateRange from '../../utils/limit-date-range';
import { useGetUserSettingsQuery } from '../../../user/api/user-api';
import { UserSettings } from '../../../user/models/user-settings';
import { mapDateSearchParams } from '../../../sign-requests/utils/map-date-search-params';
import { parseServerDateFormat } from '../../../../shared/utils/format-date/format-date';
import { openModal } from '../../../ui/store/ui-slice';
import { exportTraceLogsModalId } from '../../../../shared/constants/constants';
import { ExportType } from '../../../../shared/components/export-modal/export-type';
import { useDispatch } from 'react-redux';
import ExportModal from '../../../../shared/components/export-modal/export-modal';
import TemplateSelect from '../../../templates/components/template-select/template-select';
import styles from './sign-requests-graph.module.css';

const SignRequestsGraph = (): JSX.Element => {
  const { t } = useTranslation(['dashboard', 'common']);
  const { data: userSettings } = useGetUserSettingsQuery();
  const dispatch = useDispatch();
  const [showDataLabels, setShowDataLabels] = useState(false);

  const [form] = Form.useForm<{
    clientUsers: string[];
    dateInterval: Date[];
    template: string;
  }>();

  const initialValues: FormValues<typeof form> = {
    clientUsers: [],
    dateInterval: [subWeeks(new Date(), 1), new Date()],
    template: '',
  };

  const [getDashboardSignedDocuments, { data: signedDocuments, isFetching }] = useLazyGetDashboardSignRequestsQuery();

  const submitForm = (formData: FormValues<typeof form>) => {
    const payload = {
      clientUsers: formData.clientUsers,
      template: formData.template,
      ...mapDateSearchParams(formData.dateInterval.toString()),
    };

    getDashboardSignedDocuments(payload);
  };

  const getFormattedPayload = () => {
    const formData = form.getFieldsValue();
    const payload = {
      template: formData.template,
      clientUsers: formData.clientUsers?.join(',') || '',
      ...mapDateSearchParams(formData.dateInterval.toString()),
    };
    return new URLSearchParams(payload).toString();
  };

  const chartData = useMemo(
    () => dashboardStatisticsToChartData(signedDocuments || [], userSettings?.[UserSettings.DateFormat]),
    [signedDocuments, userSettings],
  );
  useEffect(() => {
    form.submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const limitClientUserSelect = () => {
    form.setFieldsValue({
      clientUsers: form.getFieldValue('clientUsers').slice(0, 20),
    });
  };

  const totalSum = useMemo(
    () =>
      (signedDocuments || []).reduce(
        (doc, item) => doc + (item.dataSet || []).reduce((a, b) => a + Number(b.count), 0),
        0,
      ),
    [signedDocuments],
  );

  return (
    <Card
      title={t('chart.signedDocuments')}
      extra={<Trans i18nKey="chart.total" t={t} ns="dashboard" values={{ count: totalSum }} />}
      className="dashboard-card"
    >
      <Form onFinish={submitForm} form={form} initialValues={initialValues} layout="vertical">
        <Row gutter={16} justify="start">
          <Col xs={24} md={8} lg={7} xl={6} xxl={5}>
            <Form.Item name="dateInterval" label={t('label.date')}>
              <DatePicker.RangePicker
                style={{ width: '100%' }}
                disabledDate={limitDateRange}
                format={parseServerDateFormat(userSettings?.[UserSettings.DateFormat])}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8} lg={10} xl={5}>
            <Form.Item name="clientUsers" label={t('label.user')}>
              <ClientUsersSelect mode="multiple" onSelect={limitClientUserSelect} maxTagCount={1} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8} lg={7} xl={5}>
            <Form.Item name="template" label={t('label.template')}>
              <TemplateSelect />
            </Form.Item>
          </Col>
          <Col flex="auto">
            <Row gutter={16} justify="space-between" className={styles.actionsRow}>
              <Col flex="auto">
                <Row gutter={16}>
                  <Col>
                    <CustomButton type="primary" htmlType="submit" loading={isFetching}>
                      {t('action.filter', { ns: 'common' })}
                    </CustomButton>
                  </Col>
                  <Col>
                    <CustomButton type="textBg" htmlType="submit" onClick={() => form.resetFields()}>
                      {t('action.clearFilters', { ns: 'common' })}
                    </CustomButton>
                  </Col>
                </Row>
              </Col>
              <Col>
                <CustomButton
                  type="primaryLight"
                  onClick={() =>
                    dispatch(
                      openModal({
                        id: exportTraceLogsModalId,
                        props: {
                          type: ExportType.Dashboard,
                          customParams: getFormattedPayload(),
                        },
                      }),
                    )
                  }
                >
                  {t('action.export', { ns: 'common' })}
                </CustomButton>
              </Col>
            </Row>
          </Col>
        </Row>
      </Form>
      <div className={styles.switchWrapper}>
        <span className={styles.switchLabel}>{t('label.showValues')}</span>
        <Switch checked={showDataLabels} onChange={setShowDataLabels} />
      </div>
      <LineChart data={chartData} showDataLabels={showDataLabels} />
      <ExportModal />
    </Card>
  );
};

export default SignRequestsGraph;
